import React, { useEffect, useMemo, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Printer, Download, X, Tags, Trash2 } from "lucide-react";
import { FORMATS, FORMAT_LABEL, analyse, printLabels, downloadPng, downloadSvg, labelsPdf } from "@/lib/barcode";

// Renders the barcode for the exact stored value; never alters the value.
export function BarcodePreview({ value, format = "auto", height = 48, onClick, testid = "barcode-preview" }) {
  const ref = useRef(null);
  const info = useMemo(() => analyse(value, format), [value, format]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!ref.current || !info.format || !info.valid) return;
    let ok = true;
    try { JsBarcode(ref.current, String(value), { format: info.format, height, width: 1.6, margin: 4, fontSize: 13, font: "monospace", valid: (v) => { ok = v; } }); }
    catch { ok = false; }
    setFailed(!ok);
  }, [value, info.format, info.valid, height]);
  if (!info.format) return <div className="text-xs text-slate-400" data-testid={`${testid}-empty`}>No barcode</div>;
  return (
    <div data-testid={testid}>
      {info.valid && !failed && (
        <button type="button" onClick={onClick} disabled={!onClick} title={onClick ? "Open barcode" : undefined}
          className={`block rounded-md border border-slate-200 bg-white p-1 ${onClick ? "hover:border-indigo-400 hover:shadow-sm cursor-zoom-in transition-colors" : "cursor-default"}`}
          data-testid={`${testid}-open`}>
          <svg ref={ref} className="max-w-full h-auto" />
        </button>
      )}
      <div className="mt-1 flex items-center gap-2 text-[11px]">
        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600" data-testid={`${testid}-format`}>{FORMAT_LABEL[info.format]}</span>
        {(info.warning || failed) && (
          <span className={`inline-flex items-center gap-1 ${info.valid && !failed ? "text-amber-700" : "text-rose-600"}`} data-testid={`${testid}-warning`}>
            <AlertTriangle className="w-3 h-3" />{failed ? "Cannot encode this value in the selected format" : info.warning}
          </span>
        )}
      </div>
    </div>
  );
}

export function FormatSelect({ value, onChange, testid = "barcode-format" }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700">
      {FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
    </select>
  );
}

const printOrWarn = (items) => {
  const r = printLabels(items);
  if (r === null) toast.error("Pop-up blocked — allow pop-ups to print labels");
  else if (r === false) toast.error("Nothing to print");
};

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function BarcodeDialog({ product, initialFormat = "auto", onClose }) {
  const [format, setFormat] = useState(initialFormat);
  const [qty, setQty] = useState(1);
  const info = analyse(product.barcode, format);
  const item = { title: product.title, sku: product.sku, barcode: product.barcode, format: info.format, qty: Math.min(500, Math.max(1, Number(qty) || 1)) };
  const name = `${product.sku || product.legacy_id}-${product.barcode}`;
  const can = info.valid;
  return (
    <Modal title="Barcode" onClose={onClose} testid="barcode-dialog">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
        <div className="text-sm font-semibold text-slate-900" data-testid="barcode-dialog-name">{product.title}</div>
        {product.sku && <div className="text-xs text-slate-500 mt-0.5" data-testid="barcode-dialog-sku">SKU {product.sku}</div>}
        <div className="mt-3 flex justify-center"><BarcodePreview value={product.barcode} format={format} height={80} testid="barcode-dialog-preview" /></div>
        <div className="mt-2 font-mono text-sm tracking-wider text-slate-800" data-testid="barcode-dialog-number">{product.barcode}</div>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500">Format<div className="mt-1"><FormatSelect value={format} onChange={setFormat} testid="barcode-dialog-format" /></div></label>
        <label className="text-xs text-slate-500">Labels<Input type="number" min="1" max="500" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 h-8 w-24" data-testid="barcode-dialog-qty" /></label>
        <span className="text-[11px] text-slate-400 pb-2">50 × 25 mm label</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button disabled={!can} onClick={() => printOrWarn([item])} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="barcode-print"><Printer className="w-4 h-4" /> Print</Button>
        <Button variant="outline" disabled={!can} onClick={() => labelsPdf([item], name)} className="gap-1.5" data-testid="barcode-download-pdf"><Download className="w-4 h-4" /> PDF label</Button>
        <Button variant="outline" disabled={!can} onClick={() => downloadPng(product.barcode, info.format, name)} className="gap-1.5" data-testid="barcode-download-png">PNG</Button>
        <Button variant="outline" disabled={!can} onClick={() => downloadSvg(product.barcode, info.format, name)} className="gap-1.5" data-testid="barcode-download-svg">SVG</Button>
      </div>
    </Modal>
  );
}

// products: rows from the Products list ({legacy_id,title,sku,barcode}).
export function BulkLabelsDialog({ products, onClose, onRemove }) {
  const [qty, setQty] = useState({});
  const rows = products.map((p) => ({ p, info: analyse(p.barcode, "auto") }));
  const ready = rows.filter((r) => r.info.valid);
  const items = ready.map(({ p, info }) => ({ title: p.title, sku: p.sku, barcode: p.barcode, format: info.format, qty: Math.min(500, Math.max(1, Number(qty[p.legacy_id] ?? 1) || 1)) }));
  const total = items.reduce((s, i) => s + i.qty, 0);
  return (
    <Modal title="Print barcode labels" onClose={onClose} testid="bulk-labels-dialog">
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
        {rows.map(({ p, info }) => (
          <div key={p.legacy_id} className="flex items-center gap-3 px-3 py-2" data-testid={`bulk-label-row-${p.legacy_id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate">{p.title}</div>
              <div className="text-[11px] text-slate-500 font-mono">{p.sku ? `SKU ${p.sku} · ` : ""}{p.barcode || "—"}</div>
              {!info.format && <div className="text-[11px] text-rose-600" data-testid={`bulk-label-skip-${p.legacy_id}`}>No barcode — skipped</div>}
              {info.format && <div className="text-[11px] text-slate-400">{FORMAT_LABEL[info.format]}{info.warning ? ` · ${info.warning}` : ""}</div>}
            </div>
            <Input type="number" min="1" max="500" disabled={!info.valid} value={qty[p.legacy_id] ?? 1}
              onChange={(e) => setQty((s) => ({ ...s, [p.legacy_id]: e.target.value }))} className="h-8 w-20" data-testid={`bulk-label-qty-${p.legacy_id}`} />
            <button onClick={() => onRemove(p.legacy_id)} className="text-slate-400 hover:text-rose-600" data-testid={`bulk-label-remove-${p.legacy_id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {!rows.length && <div className="px-3 py-6 text-center text-sm text-slate-400">No products selected</div>}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500 mr-auto" data-testid="bulk-label-total">{total} label{total === 1 ? "" : "s"} · 50 × 25 mm</span>
        <Button variant="outline" disabled={!items.length} onClick={() => labelsPdf(items, "barcode-labels")} className="gap-1.5" data-testid="bulk-labels-pdf"><Download className="w-4 h-4" /> PDF</Button>
        <Button disabled={!items.length} onClick={() => printOrWarn(items)} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="bulk-labels-print"><Tags className="w-4 h-4" /> Print labels</Button>
      </div>
    </Modal>
  );
}
